import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useEffect, useRef } from "react";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { Button } from "@progress/kendo-react-buttons";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  createCity,
  getAllCities,
  getCityById,
  updateCity,
} from "./services/city.services";
import { clearCityDetails, closeCityDrawerOpen } from "./citySlice";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import { getAllActiveCountries } from "../country/services/country.services";
import { findAllActiveStates } from "../state/services/state.services";
import { IState } from "../state/stateModel";
import { findIndex } from "lodash";
import { Typography } from "@progress/kendo-react-common";
import { STATUSARRAY } from "../../_contstants/common";
import { openCountryDrawerOpen } from "../country/countrySlice";
import { openStateDrawerOpen } from "../state/stateSlice";

interface StateChangeWatcherProps {
  formRenderProps: FormRenderProps;
  StateList: IState[];
}
const StateChangeWatcher: React.FC<StateChangeWatcherProps> = ({
  StateList,
  formRenderProps,
}) => {
  const state_id = formRenderProps.valueGetter("state_id");
  useEffect(() => {
    const StateID = state_id ? state_id : null;
    const stateIndex = findIndex(StateList, { id: StateID });
    if (stateIndex > -1) {
      const state = StateList[stateIndex];
      formRenderProps.onChange("country_id", { value: state.country_id });
    } else {
      formRenderProps.onChange("state_id", { value: "" });
      formRenderProps.onChange("country_id", { value: "" });
    }
  }, [state_id]);
  return null;
};

const CreateCity: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CountryList = useAppSelector((state) => state.country.CountryList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const CityDetail = useAppSelector((state) => state.city.CityDetail);
  const city_guid = useAppSelector((state) => state.muidrawer.data);
  const FilterCountry = useAppSelector((state) => state.country.FilterCountry);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const formLoading = useAppSelector((state) => state.city.formLoading);
  const isIndirect = useAppSelector((state) => state.muidrawer.isIndirect);
  const cityDrawerOpen = useAppSelector((state) => state.city.cityDrawerOpen);
  const getpayload = {
    ...FilterCountry,
    Limit: +pageLimit,
    Skip: +currentPage * +pageLimit,
  };
  useEffect(() => {
    dispatch(getAllActiveCountries());
    dispatch(findAllActiveStates());
    return () => {
      dispatch(clearCityDetails());
      if (cityDrawerOpen) {
        dispatch(closeCityDrawerOpen());
      } else {
        dispatch(closed());
      }
    };
  }, []);

  useEffect(() => {
    if (city_guid && !cityDrawerOpen) {
      dispatch(getCityById(city_guid));
    }
  }, [city_guid]);

  const handleSubmit = async (values: any) => {
    try {
      if (!city_guid) {
        const payload = {
          city_name: values.city_name,
          state_id: values.state_id,
          country_id: values.country_id,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(createCity(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (cityDrawerOpen) {
            dispatch(closeCityDrawerOpen());
          } else {
            dispatch(closed());
          }
          dispatch(clearCityDetails());
          isIndirect || cityDrawerOpen
            ? dispatch(getAllActiveCountries())
            : dispatch(getAllCities(getpayload));
        }
      } else {
        const payload = {
          city_guid: city_guid,
          city_name: values.city_name,
          state_id: values.state_id,
          country_id: values.country_id,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(updateCity(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (cityDrawerOpen) {
            dispatch(closeCityDrawerOpen());
          } else {
            dispatch(closed());
          }
          dispatch(clearCityDetails());
          isIndirect || cityDrawerOpen
            ? dispatch(getAllActiveCountries())
            : dispatch(getAllCities(getpayload));
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    if (cityDrawerOpen) {
      dispatch(closeCityDrawerOpen());
    } else {
      dispatch(closed());
    }
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={CityDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem>
              <Typography.h4 className="m-0">
                {city_guid ? "Update City" : "Add City"}
              </Typography.h4>
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="city_name"
                label="City"
                component={FormTextField}
                placeholder="i.e. Ahmedabad"
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name={"state_id"}
                label={"State"}
                placeholder="Select State"
                isAddNew={true}
                isIndirect={true}
                isDrawerInDrawer={true}
                drawerName="stateForm"
                CustomDrawerOpenFunction={openStateDrawerOpen}
                component={FormSelectionField}
                validator={requiredValidator}
                astrike={true}
                options={StateList?.map((state: any) => {
                  return {
                    value: state.id,
                    label: state.state_name,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name={"country_id"}
                label={"Country"}
                component={FormSelectionField}
                placeholder="Select Country"
                isAddNew={true}
                isIndirect={true}
                isDrawerInDrawer={true}
                drawerName="countryForm"
                CustomDrawerOpenFunction={openCountryDrawerOpen}
                validator={requiredValidator}
                astrike={true}
                disabled={true}
                options={CountryList.map((country: any) => {
                  return {
                    value: country.id,
                    label: country.country_name,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name={"isactive"}
                component={FormSelectionField}
                validator={requiredValidator}
                placeholder="Select Status"
                astrike={true}
                label={"Status"}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "10px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={city_guid ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
          <StateChangeWatcher
            StateList={StateList}
            formRenderProps={formRenderProps}
          />
        </FormElement>
      )}
    />
  );
};

export default CreateCity;
